'use client';

import Link from 'next/link';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Favourities } from '@/components/addToFavourites/addToFavourities';
import { Skeleton } from '@/components/ui/skeleton';
import { sendGTMEvent } from '@/events/GTM/current/GTMScript';
// import { sendGTMEvent } from '@next/third-parties/google';
import { prepareSelectItemEvent } from '@/events/GTM/gtmEventModels';
import { AddToCart } from '@/features/products/components/addToCart/addToCart';
import { CustomRating } from '@/features/products/components/customRating/customRating';
import { ProductCardImage } from '@/features/products/components/listingProductCard/productCardImage/productCardImage';
import { ListingProductCardTaxonData } from '@/features/products/components/listingProductCard/types';
import { ProductChips } from '@/features/products/components/productChips/productChips';
import { ProductCustomisation } from '@/features/products/components/productCustomisation/productCustomisation';
import { ProductPrice } from '@/features/products/components/productPrice/productPrice';
import { getProductBySlug } from '@/features/products/data/productData';
import { productMainImage, showProductName } from '@/features/products/data/utils';
import {
  mapProductDTOToProductListingItem,
  ProductListingItem,
  ProductType
} from '@/features/products/types/product';
import { ProductNameType } from '@/features/products/types/productNames';
import { yieldOrTimeout } from '@/lib/yieldOrTimeout';

interface ListingProductCardProps extends ProductListingItem {
  taxonData?: ListingProductCardTaxonData;
  authToken?: string;
  priority?: boolean;
}

export const ListingProductCard = memo(
  (props: ListingProductCardProps) => {
    ListingProductCard.displayName = 'ListingProductCard';

    const cardRef = useRef<HTMLDivElement>(null);
    const [fixedHeight, setFixedHeight] = useState<number | undefined>(undefined);
    const [currentProduct, setCurrentProduct] = useState<ProductListingItem>(props);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      setCurrentProduct(props);
    }, [props]);

    useEffect(() => {
      if (!isLoading) {
        setFixedHeight(undefined);
      }
    }, [isLoading]);

    const getNewVariant = useCallback(
      async (slug: string) => {
        if (slug === currentProduct.slug) return;

        if (cardRef.current) {
          setFixedHeight(cardRef.current.clientHeight);
        }

        setIsLoading(true);
        try {
          const response = await getProductBySlug(slug);
          setCurrentProduct(mapProductDTOToProductListingItem(response));
        } catch (err) {
          console.error('Failed to fetch variant', err);
        } finally {
          setIsLoading(false);
        }
      },
      [currentProduct.slug]
    );

    const sendGTMEventHandler = () => {
      if (props?.taxonData) {
        yieldOrTimeout()
          .then(() => {
            if (props?.taxonData) {
              sendGTMEvent({ ecommerce: null });
              const eventData = prepareSelectItemEvent({
                product: currentProduct,
                taxonData: {
                  slug: props.taxonData.taxonSlug,
                  name: props.taxonData.taxonName,
                  breadcrumb: props.taxonData.taxonBreadcrumbs
                },
                token: props.authToken ? props.authToken : ''
              });
              sendGTMEvent(eventData);
            }
          })
          .catch();
      }
    };

    const isPersonalizable = currentProduct.group && currentProduct.group.length > 1;
    const imgObj = productMainImage(currentProduct.images);

    return (
      <Link
        onClick={() => sendGTMEventHandler()}
        href={`/pl/o/${currentProduct?.slug || ''}`}
        className="no-underline w-full max-w-82 bg-(--mui-palette-background-paper) rounded-lg shadow-(--mui-shadows-1)"
      >
        <article
          ref={cardRef}
          style={{ height: fixedHeight }}
          className="flex flex-col h-full rounded-lg p-3 gap-3 relative"
        >
          {isLoading ? (
            <>
              <div className="max-w-full max-h-76 sm:max-h-65 h-full w-full relative">
                <Skeleton variant="rectangular" className="rounded-lg size-full" />
              </div>

              <div className="flex flex-col pr-2 w-full h-full justify-between gap-2 mt-1">
                <div>
                  <div className="flex flex-col gap-3 md:gap-2">
                    <div className="min-h-5 flex items-center">
                      <Skeleton variant="text" width="30%" height={20} />
                    </div>

                    <div>
                      <Skeleton variant="text" width="90%" height={32} />
                      <Skeleton variant="text" width="60%" height={32} />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Skeleton variant="rounded" width={80} height={32} />
                    <Skeleton variant="rounded" width={80} height={32} />
                  </div>
                </div>
                <div className="flex flex-col justify-end h-full">
                  <div className="flex justify-between items-center w-full mt-1">
                    <div className="w-1/2">
                      <Skeleton variant="text" width="80%" height={40} />
                    </div>
                    <Skeleton variant="rounded" width={48} height={48} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="max-w-full w-full relative">
                <ProductCardImage
                  alt={currentProduct.name}
                  src={imgObj?.path || imgObj?.fallbackPath || '/notavailable.jpg'}
                  priority={props.priority}
                />
                <ProductChips
                  product={currentProduct}
                  size="small"
                  classNames="flex-col top-2.5! left-2.5!"
                />
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center absolute top-2.5 right-2.5"
                >
                  <Favourities product={currentProduct} className="bg-white" />
                </div>
              </div>

              <div className="flex flex-col pr-2 w-full h-full justify-between gap-2">
                <div>
                  <div className="flex flex-col gap-3 md:gap-2">
                    <div className="min-h-5">
                      {!!currentProduct?.reviewsCount ? (
                        <CustomRating
                          value={currentProduct?.averageRating || 0}
                          totalRatings={currentProduct.reviewsCount}
                          size="small"
                        />
                      ) : null}
                    </div>

                    <p className="text-lg leading-[175%] font-normal">
                      {showProductName(currentProduct, ProductNameType.Listing)}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <ProductCustomisation
                      product={currentProduct}
                      maxElements={3}
                      isPersonalizable={isPersonalizable}
                      getNewVariant={getNewVariant}
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-end h-full">
                  <div className="relative flex justify-between items-end w-full mt-1">
                    <ProductPrice
                      promotion={!!currentProduct?.promotion}
                      lowestPriceBeforeDiscount={currentProduct?.variant?.lowestPriceBeforeDiscount}
                      price={currentProduct?.variant?.price}
                      originalPrice={currentProduct?.variant?.originalPrice}
                    />
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        const link = (e.target as HTMLElement).closest('a[href]');
                        if (!link || !e.currentTarget.contains(link)) e.preventDefault();
                      }}
                      className="absolute bottom-0 right-0"
                    >
                      <AddToCart
                        product={currentProduct}
                        secondaryVariant
                        onlyMobileView
                        iconClassNames="py-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </article>
      </Link>
    );
  },
  (prev, next) => prev.id === next.id
);
